import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcLeite } from './CalcleiteContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Relatorio.css';
import Config from '../../config/config.json';
import tags from '../../config/tags.json';

const Relatorio = () => {
  const { dados, setDados } = useCalcLeite();
  const navigate = useNavigate();

  // Funções auxiliares para formatação consistente
  const formatCurrency = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatNumber = (value) => parseFloat(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Extrai os dados de forma segura, com fallbacks para evitar erros
  const producao = dados.producao || {};
  const resProducao = dados.resultadosProducao || {};
  const resCapital = dados.resultadosCapital || {};
  const resCustos = dados.resultadosCustos || {};
  const resReceita = dados.resultadosReceita || {};

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const dataAnalise = producao.dataSelecionada;

    // Cabeçalho com fundo azul
    doc.setFillColor(30, 144, 255); // #1e90ff
    doc.rect(0, 0, 210, 40, 'F'); // Cabeçalho com 40mm de altura
    doc.setTextColor(255, 255, 255); // Texto branco
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text("Relatório Final - CalcLeite", 105, 20, { align: 'center' });

    // Data centralizada abaixo do título
    doc.setTextColor(200, 200, 200); // Cinza claro
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Data: ${dataAnalise ? new Date(dataAnalise).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'N/A'}`, 105, 30, { align: 'center' });

    // Adicionar imagens no cabeçalho
    const custosAgroImg = Config.colors.base64.custosagro;
    const appImg = Config.colors.base64[tags.tags.app];
    if (custosAgroImg) {
      doc.addImage(custosAgroImg, 'PNG', 10, 7.5, 25, 25); // Logo CustosAgro mais à esquerda
    }
    if (appImg) {
      doc.addImage(appImg, 'PNG', 175, 7.5, 25, 25); // Logo do app mais à direita
    }

    // Tabela de Produção e Produtividade
    let y = 50;
    doc.setTextColor(80, 80, 80);
    autoTable(doc, {
      startY: y,
      head: [['Indicador', 'Valor']],
      body: [
        ['Leite / Hectare de Pasto', `${formatNumber(resProducao.leitePastagem)} L/ha`],
        ['Leite / Hectare de Volumoso', `${formatNumber(resProducao.leiteVolumoso)} L/ha`],
        ['Leite / Vaca no Rebanho / Dia', `${formatNumber(resProducao.leiteVacaRebanho)} L/cab/dia`],
        ['Leite / Vaca em Lactação / Dia', `${formatNumber(resProducao.leiteVacaLactacao)} L/cab/dia`],
        ['Relação Lactação/Rebanho', `${formatNumber(resProducao.relacaoLactacaoRebanho)} %`],
        ['Leite / Colaborador / Dia', `${formatNumber(resProducao.leiteColaborador)} L/colab/dia`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [44, 62, 80] },
    });
    y = doc.lastAutoTable.finalY + 10;

    // Tabela de Capital Investido e Depreciação
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    autoTable(doc, {
      startY: y,
      head: [['Indicador', 'Valor']],
      body: [
        ['Capital Total Investido', formatCurrency(resCapital.capitalTotalInvestido)],
        ['Rentabilidade do Capital', formatCurrency(resCapital.rentabilidadeCapital)],
        ['Rentabilidade por Litro', formatCurrency(resCapital.rentabilidadeCapitalLitro)],
        ['Depreciação', formatCurrency(resCapital.depreciacao)],
        ['Depreciação por Litro', formatCurrency(resCapital.depreciacaoLitro)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [44, 62, 80] },
    });
    y = doc.lastAutoTable.finalY + 10;

    // Tabela de Custos de Produção
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    autoTable(doc, {
      startY: y,
      head: [['Indicador', 'Valor']],
      body: [
        ['Custo Operacional', formatCurrency(resCustos.custoOperacional)],
        ['Custo Total', formatCurrency(resCustos.custoTotal)],
        ['Custo Operacional por Litro', `${formatCurrency(resCustos.custoOperacionalLitro)} /L`],
        ['Custo Total por Litro', `${formatCurrency(resCustos.custoTotalLitro)} /L`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [44, 62, 80] },
    });
    y = doc.lastAutoTable.finalY + 10;

    // Tabela de Receita e Rentabilidade
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    autoTable(doc, {
      startY: y,
      head: [['Indicador', 'Valor']],
      body: [
        ['Receita Bruta Total', formatCurrency(resReceita.receitaBrutaTotal)],
        ['Lucro Bruto (Operacional)', formatCurrency(resReceita.lucroBruto)],
        ['Lucro Líquido', formatCurrency(resReceita.lucroLiquido)],
        ['Lucro Líquido por Litro', formatCurrency(resReceita.lucroLiquidoLitro)],
        ['Ponto de Equilíbrio', `${formatNumber(resReceita.pontoEquilibrio)} L`],
        ['Relação Rentabilidade (Leite vs Mercado)', `${formatNumber(resReceita.relacaoRentabilidade)} %`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [44, 62, 80] },
    });

    // Adicionar numeração de páginas
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(`Relatório gerado por CustosAgro - Página ${i} de ${pageCount}`, 105, 287, { align: 'center' });
    }
    
    doc.save("relatorio_final_calcleite.pdf");
  };

  const handleLimparTodosDados = () => {
    if (window.confirm("ATENÇÃO! Isso limpará TODOS os dados inseridos em TODAS as telas do CalcLeite. Deseja continuar?")) {
      setDados({}); // Limpa todo o objeto de dados
      localStorage.removeItem('calcLeiteData'); // Remove do localStorage
      alert("Todos os dados do CalcLeite foram limpos.");
      navigate('/calcleite/app'); // Volta para a tela principal
    }
  };

  return (
    <div className="relatorio-container">
      <div className="relatorio-card">
        <h1>Relatório Final</h1>
        <p className="relatorio-subtitle">
          Resumo consolidado dos seus resultados para a data de <strong>{producao.dataSelecionada ? new Date(producao.dataSelecionada).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'N/A'}</strong>.
        </p>

        <div className="relatorio-grid">
          {/* Seção de Produção */}
          <div className="relatorio-section">
            <h2>Produção e Produtividade</h2>
            <div className="relatorio-item"><span>Leite / Hectare de Pasto</span><strong>{formatNumber(resProducao.leitePastagem)} L/ha</strong></div>
            <div className="relatorio-item"><span>Leite / Hectare de Volumoso</span><strong>{formatNumber(resProducao.leiteVolumoso)} L/ha</strong></div>
            <div className="relatorio-item"><span>Leite / Vaca no Rebanho / Dia</span><strong>{formatNumber(resProducao.leiteVacaRebanho)} L/cab/dia</strong></div>
            <div className="relatorio-item"><span>Leite / Vaca em Lactação / Dia</span><strong>{formatNumber(resProducao.leiteVacaLactacao)} L/cab/dia</strong></div>
            <div className="relatorio-item"><span>Relação Lactação/Rebanho</span><strong>{formatNumber(resProducao.relacaoLactacaoRebanho)} %</strong></div>
            <div className="relatorio-item"><span>Leite / Colaborador / Dia</span><strong>{formatNumber(resProducao.leiteColaborador)} L/colab/dia</strong></div>
          </div>

          {/* Seção de Capital */}
          <div className="relatorio-section">
            <h2>Capital e Depreciação</h2>
            <div className="relatorio-item"><span>Capital Total Investido</span><strong>{formatCurrency(resCapital.capitalTotalInvestido)}</strong></div>
            <div className="relatorio-item"><span>Rentabilidade do Capital</span><strong>{formatCurrency(resCapital.rentabilidadeCapital)}</strong></div>
            <div className="relatorio-item"><span>Rentabilidade por Litro</span><strong>{formatCurrency(resCapital.rentabilidadeCapitalLitro)}</strong></div>
            <div className="relatorio-item"><span>Depreciação</span><strong>{formatCurrency(resCapital.depreciacao)}</strong></div>
            <div className="relatorio-item"><span>Depreciação por Litro</span><strong>{formatCurrency(resCapital.depreciacaoLitro)}</strong></div>
          </div>

          {/* Seção de Custos */}
          <div className="relatorio-section">
            <h2>Custos de Produção</h2>
            <div className="relatorio-item"><span>Custo Operacional</span><strong>{formatCurrency(resCustos.custoOperacional)}</strong></div>
            <div className="relatorio-item"><span>Custo Total</span><strong>{formatCurrency(resCustos.custoTotal)}</strong></div>
            <div className="relatorio-item"><span>Custo Operacional por Litro</span><strong>{formatCurrency(resCustos.custoOperacionalLitro)}</strong></div>
            <div className="relatorio-item"><span>Custo Total por Litro</span><strong>{formatCurrency(resCustos.custoTotalLitro)}</strong></div>
          </div>
          
          {/* Seção de Receita */}
          <div className="relatorio-section">
            <h2>Receita e Rentabilidade</h2>
            <div className="relatorio-item"><span>Receita Bruta Total</span><strong>{formatCurrency(resReceita.receitaBrutaTotal)}</strong></div>
            <div className="relatorio-item"><span>Lucro Bruto (Operacional)</span><strong>{formatCurrency(resReceita.lucroBruto)}</strong></div>
            <div className="relatorio-item"><span>Lucro Líquido</span><strong>{formatCurrency(resReceita.lucroLiquido)}</strong></div>
            <div className="relatorio-item"><span>Lucro Líquido por Litro</span><strong>{formatCurrency(resReceita.lucroLiquidoLitro)}</strong></div>
            <div className="relatorio-item"><span>Ponto de Equilíbrio</span><strong>{formatNumber(resReceita.pontoEquilibrio)} L</strong></div>
            <div className="relatorio-item"><span>Relação Rentabilidade (Leite vs Mercado)</span><strong>{formatNumber(resReceita.relacaoRentabilidade)} %</strong></div>
          </div>
        </div>

        <div className="relatorio-actions">
          <button className="relatorio-button pdf" onClick={handleDownloadPDF}>Baixar Relatório Completo (PDF)</button>
        </div>

        <div className="relatorio-navigation">
          <button className="relatorio-button back" onClick={() => navigate('/calcleite/app/receita')}>Voltar para Receita</button>
          <button className="relatorio-button clean" onClick={handleLimparTodosDados}>Limpar Todos os Dados</button>
        </div>
      </div>
    </div>
  );
};

export default Relatorio;